import { communicationService } from '@ad-engine/communication';
import {
	AdSlotEvent,
	BaseServiceSetup,
	context,
	Dictionary,
	InstantConfigService,
	targetingService,
	utils,
} from '@ad-engine/core';
import {
	BIDDERS_BIDDING_DONE,
	BIDDERS_BIDS_REFRESH,
	BIDDERS_CALL_PER_GROUP,
} from '../communication/events/events-bidders';
import { A9Provider } from './a9';
import {
	defaultSlotBidGroup,
	findSlotNamesForBidGroup,
	getSlotBidGroupByName,
} from './bidder-helper';
import { PrebidProvider } from './prebid';

interface BiddersProviders {
	a9?: A9Provider;
	prebid?: PrebidProvider;
}

const logGroup = 'bidders';

export class Bidders extends BaseServiceSetup {
	private biddersProviders: { [key: string]: BiddersProviders } = {};

	constructor(
		protected instantConfig: InstantConfigService,
		protected globalTimeout: utils.GlobalTimeout | null = null,
	) {
		super(instantConfig, globalTimeout);

		communicationService.onSlotEvent(AdSlotEvent.VIDEO_AD_REQUESTED, ({ slot }) => {
			slot.updateWinningPbBidderDetails();
		});
		communicationService.onSlotEvent(AdSlotEvent.VIDEO_AD_USED, ({ slot }) => {
			this.updateSlotTargeting(slot.getSlotName());
		});
		communicationService.on(
			BIDDERS_BIDS_REFRESH,
			({ refreshedSlotNames }) => {
				refreshedSlotNames.forEach((slotName) => this.updateSlotTargeting(slotName));
			},
			false,
		);

		communicationService.on(
			BIDDERS_CALL_PER_GROUP,
			({ group, callback, shouldUpdateTargetingBeforeCallback }) => {
				this.callByBidGroup(group).then(() => {
					if (shouldUpdateTargetingBeforeCallback) {
						this.updateSlotTargetingForBidGroup(group, callback);
					} else if (callback) {
						callback();
						utils.logger(logGroup, `${group} - callback`);
					}
				});
			},
			false,
		);
	}

	getName(): string {
		return 'bidders';
	}

	isEnabled(): boolean {
		return context.get('bidders.prebid.enabled') || context.get('bidders.a9.enabled');
	}

	applyTargetingParams(slotName, targeting): void {
		Object.keys(targeting).forEach((key) => targetingService.set(key, targeting[key], slotName));
	}

	async getBidParameters(slotName): Promise<Dictionary> {
		const slotParams = {};
		const slotBidGroup = getSlotBidGroupByName(slotName);

		await Promise.all(
			this.getBiddersProviders(slotBidGroup).map(async (provider) => {
				if (provider && provider.wasCalled()) {
					const params = await provider.getSlotTargetingParams(slotName);

					Object.assign(slotParams, params);
				}
			}),
		);

		return slotParams;
	}

	resetTargetingKeys(slotName): void {
		const slotBidGroup = getSlotBidGroupByName(slotName);

		this.getBiddersProviders(slotBidGroup).forEach((provider) => {
			provider.getTargetingKeys(slotName).forEach((key) => {
				targetingService.remove(key, slotName);
			});
		});

		utils.logger(logGroup, 'resetTargetingKeys', slotName);
	}

	call(): Promise<void> {
		return this.callByBidGroup(defaultSlotBidGroup);
	}

	callByBidGroup(group: string): Promise<void> {
		const config = context.get('bidders') || {};
		const promise = utils.createExtendedPromise();
		const timeout = this.getBidderTimeout(config, group);

		this.biddersProviders[group] = this.biddersProviders[group] || {};

		if (config.prebid && config.prebid.enabled) {
			this.biddersProviders[group].prebid = new PrebidProvider(config.prebid, timeout, group);
		}

		if (A9Provider.isEnabled()) {
			this.biddersProviders[group].a9 = new A9Provider(config.a9, timeout, group);
		} else {
			utils.logger(logGroup, `Group: ${group} - A9 has been disabled`);
		}

		if (!this.getBiddersProviders(group).length) {
			utils.logger(
				logGroup,
				`Group: ${group} - resolving call() promise because of no bidder providers`,
			);
			return Promise.resolve();
		}

		this.getBiddersProviders(group).forEach((provider) => {
			provider.addResponseListener(() => {
				if (this.hasAllResponses(group)) {
					utils.logger(
						logGroup,
						`Group: ${group} - ${provider.name} - resolving call() promise because of having all responses`,
					);
					promise.resolve(null);
				}
			});

			provider.call();
		});

		utils.logger(logGroup, `Group: ${group} - returning call() promise`);
		return promise;
	}

	async updateSlotTargeting(slotName): Promise<Dictionary> {
		const bidderTargeting = await this.getBidParameters(slotName);

		this.resetTargetingKeys(slotName);
		this.applyTargetingParams(slotName, bidderTargeting);

		utils.logger(logGroup, 'updateSlotTargeting', slotName, bidderTargeting);
		communicationService.emit(BIDDERS_BIDDING_DONE, {
			slotName,
			provider: 'prebid',
		});

		return bidderTargeting;
	}

	private hasAllResponses(bidGroup: string): boolean {
		const missingProviders = Object.keys(this.getBidderProviders(bidGroup)).filter(
			(providerName) => {
				const provider = this.getBidderProviders(bidGroup)[providerName];

				if (!provider) {
					return false;
				}

				return !provider.hasResponse();
			},
		);

		return missingProviders.length === 0;
	}

	private getBiddersProviders(bidGroup: string): (A9Provider | PrebidProvider)[] {
		return Object.values(this.biddersProviders[bidGroup] || {});
	}

	private getBidderProviders(bidGroup: string): BiddersProviders {
		return this.biddersProviders[bidGroup] || {};
	}

	private getBidderTimeout(
		config: { timeout: number; timeoutByGroup: { [key: number]: number } },
		bidGroup: string,
	) {
		return config?.timeoutByGroup?.[bidGroup] || config.timeout;
	}

	private updateSlotTargetingForBidGroup(group: string, callback?: () => void) {
		const slotNames = findSlotNamesForBidGroup(group);

		const updateSlotTargetingPromises = slotNames.map((slotName) =>
			this.updateSlotTargeting(slotName),
		);

		Promise.all(updateSlotTargetingPromises)
			.catch((error) => {
				utils.logger(logGroup, 'Error updating slot targeting:', error);
			})
			.finally(() => {
				if (callback) {
					utils.logger(logGroup, `${group} - callback`);
					callback();
				}
			});
	}
}

export * from './a9/index';
export * from './prebid/dsa/handler';
export * from './prebid/intent-iq';
export * from './prebid/native';
export * from './wrappers';
